<template>
  <header class="navbar" ref="navbarRef">
    <div class="navbar-inner">
      <NuxtLink to="/" class="navbar-logo">
        <img src="/eduversity_logo.svg" alt="Qspiders Eduversity" class="logo-image" />
      </NuxtLink>

      <nav class="navbar-links">
        <NuxtLink to="/hire-from-us" class="nav-link">Hire From Us</NuxtLink>
        <NuxtLink to="/placements" class="nav-link">Placements</NuxtLink>
        <NuxtLink to="/downloads" class="nav-link">Downloads</NuxtLink>
        <NuxtLink to="/contact-us" class="nav-link nav-link-cta">Contact Us</NuxtLink>
      </nav>

      <div class="navbar-actions">
        <button class="theme-toggle" @click="toggleTheme" :aria-label="isDark ? 'Switch to light mode' : 'Switch to dark mode'">
          <!-- Sun icon (shown in dark mode) -->
          <svg v-if="isDark" xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="12" cy="12" r="5" />
            <line x1="12" y1="1" x2="12" y2="3" />
            <line x1="12" y1="21" x2="12" y2="23" />
            <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" />
            <line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
            <line x1="1" y1="12" x2="3" y2="12" />
            <line x1="21" y1="12" x2="23" y2="12" />
            <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" />
            <line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
          </svg>
          <!-- Moon icon (shown in light mode) -->
          <svg v-else xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
          </svg>
        </button>

        <button class="mobile-toggle" @click="mobileOpen = !mobileOpen" aria-label="Toggle menu">
          <span class="toggle-bar" :class="{ open: mobileOpen }"></span>
          <span class="toggle-bar" :class="{ open: mobileOpen }"></span>
          <span class="toggle-bar" :class="{ open: mobileOpen }"></span>
        </button>
      </div>
    </div>

    <Transition name="slide-down">
      <nav v-if="mobileOpen" class="mobile-menu">
        <NuxtLink to="/hire-from-us" class="mobile-link" @click="mobileOpen = false">Hire From Us</NuxtLink>
        <NuxtLink to="/placements" class="mobile-link" @click="mobileOpen = false">Placements</NuxtLink>
        <NuxtLink to="/downloads" class="mobile-link" @click="mobileOpen = false">Downloads</NuxtLink>
        <NuxtLink to="/contact-us" class="mobile-link mobile-link-cta" @click="mobileOpen = false">Contact Us</NuxtLink>
        <button class="mobile-theme-toggle" @click="toggleTheme">
          <svg v-if="isDark" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="12" cy="12" r="5" />
            <line x1="12" y1="1" x2="12" y2="3" />
            <line x1="12" y1="21" x2="12" y2="23" />
            <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" />
            <line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
            <line x1="1" y1="12" x2="3" y2="12" />
            <line x1="21" y1="12" x2="23" y2="12" />
            <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" />
            <line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
          </svg>
          <svg v-else xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
          </svg>
          <span>{{ isDark ? 'Light Mode' : 'Dark Mode' }}</span>
        </button>
      </nav>
    </Transition>
  </header>
</template>

<script setup lang="ts">
const mobileOpen = ref(false)
const { isDark, toggleTheme } = useTheme()
</script>

<style scoped>
.navbar {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  padding: 0 1.5rem;
}

.navbar-inner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  max-width: 1380px;
  margin: 1rem auto 0;
  padding: 0.1rem 2rem;
  background: var(--nav-bg);
  backdrop-filter: blur(10px) saturate(160%);
  -webkit-backdrop-filter: blur(10px) saturate(160%);
  border: 1px solid var(--nav-border);
  border-radius: 16px;
  transition: background 0.3s ease, border-color 0.3s ease, box-shadow 0.3s ease;
}

[data-theme="light"] .navbar-inner {
  box-shadow: 0 2px 16px rgba(0, 0, 0, 0.06);
}

.navbar-logo {
  display: flex;
  align-items: center;
  text-decoration: none;
  flex-shrink: 0;
}

.logo-image {
  height: 60px;
  width: auto;
  object-fit: contain;
}

.navbar-links {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  margin-left: auto;
}

.nav-link {
  position: relative;
  padding: 0.625rem 1.125rem;
  font-size: 0.9375rem;
  font-weight: 500;
  color: var(--nav-text);
  text-decoration: none;
  border-radius: 8px;
  transition: all 0.25s ease;
  white-space: nowrap;
}

.nav-link:hover {
  color: var(--nav-text-hover);
  background: var(--nav-hover-bg);
}

.nav-link-cta {
  margin-left: 0.5rem;
  padding: 0.625rem 1.25rem;
  background: linear-gradient(135deg, #ff7800, #ff4e00);
  color: #ffffff;
  font-weight: 600;
  border-radius: 8px;
  box-shadow: 0 4px 14px rgba(255, 120, 0, 0.25);
}

.nav-link-cta:hover {
  background: linear-gradient(135deg, #ff8c1a, #ff601a);
  box-shadow: 0 6px 20px rgba(255, 120, 0, 0.4);
  transform: translateY(-1px);
}

/* ── Actions container ──────────────────────── */
.navbar-actions {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-left: 1rem;
}

/* ── Theme toggle ───────────────────────────── */
.theme-toggle {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 10px;
  border: 1px solid var(--nav-border);
  background: var(--nav-hover-bg);
  color: var(--nav-text);
  cursor: pointer;
  transition: all 0.25s ease;
}

.theme-toggle:hover {
  background: var(--glass-bg);
  border-color: var(--accent-orange);
  color: var(--accent-orange);
  transform: scale(1.05);
}

.mobile-toggle {
  display: none;
  flex-direction: column;
  gap: 5px;
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  z-index: 1001;
}

.toggle-bar {
  display: block;
  width: 24px;
  height: 2px;
  background: var(--toggle-bar-bg);
  border-radius: 2px;
  transition: all 0.3s ease;
}

.toggle-bar.open:nth-child(1) {
  transform: translateY(7px) rotate(45deg);
}

.toggle-bar.open:nth-child(2) {
  opacity: 0;
}

.toggle-bar.open:nth-child(3) {
  transform: translateY(-7px) rotate(-45deg);
}

.mobile-menu {
  display: none;
  flex-direction: column;
  gap: 0.5rem;
  max-width: 1280px;
  margin: 0.5rem auto 0;
  padding: 1rem 1.5rem;
  background: var(--nav-bg);
  backdrop-filter: blur(16px) saturate(160%);
  -webkit-backdrop-filter: blur(16px) saturate(160%);
  border: 1px solid var(--nav-border);
  border-radius: 16px;
  transition: background 0.3s ease, border-color 0.3s ease;
}

[data-theme="light"] .mobile-menu {
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.08);
}

.mobile-link {
  padding: 0.75rem 1rem;
  font-size: 1rem;
  font-weight: 500;
  color: var(--nav-text);
  text-decoration: none;
  border-radius: 10px;
  transition: all 0.2s ease;
}

.mobile-link:hover {
  color: var(--nav-text-hover);
  background: var(--nav-hover-bg);
}

.mobile-link-cta {
  background: linear-gradient(135deg, #ff7800, #ff4e00);
  color: #ffffff;
  font-weight: 600;
  text-align: center;
}

.mobile-link-cta:hover {
  background: linear-gradient(135deg, #ff8c1a, #ff601a);
}

.mobile-theme-toggle {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.75rem 1rem;
  font-size: 1rem;
  font-weight: 500;
  color: var(--nav-text);
  background: none;
  border: none;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.mobile-theme-toggle:hover {
  background: var(--nav-hover-bg);
}

.slide-down-enter-active,
.slide-down-leave-active {
  transition: all 0.3s ease;
}

.slide-down-enter-from,
.slide-down-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

@media (max-width: 768px) {
  .navbar {
    padding: 0 1rem;
  }

  .navbar-inner {
    padding: 0.625rem 1.25rem;
    margin: 0.75rem auto 0;
  }

  .navbar-links {
    display: none;
  }

  .navbar-actions {
    margin-left: auto;
  }

  .mobile-toggle {
    display: flex;
  }

  .mobile-menu {
    display: flex;
    margin: 0.5rem 0 0;
  }

  .logo-image {
    height: 32px;
  }
}
</style>
